import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Car, CheckCircle, ArrowsClockwise, Eye, ShieldCheck, Percent, Lightning, Coins, Trash } from "@phosphor-icons/react";

const CATS = [
  { key: "bike", label: "Bike", emoji: "🏍️" },
  { key: "car", label: "Car", emoji: "🚗" },
  { key: "truck", label: "Truck (Goods)", emoji: "🚚" },
];

function Dollar({ label, value, onSave, testid }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-center gap-1">
        <span className="text-muted-foreground">$</span>
        <input data-testid={testid} type="number" min="0" step="0.25" defaultValue={((value ?? 0) / 100).toString()}
          onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== value) onSave(c); }}
          className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </div>
    </div>
  );
}

function DriverApprovals() {
  const qc = useQueryClient();
  const [notes, setNotes] = useState({});
  const [insp, setInsp] = useState({});
  const { data } = useQuery({ queryKey: ["admin-drivers"], queryFn: async () => (await api.get("/rides/admin/drivers")).data.drivers });
  const drivers = (data || []).slice().sort((a, b) => (a.approval_status === "pending" ? -1 : 1));

  const viewDoc = async (id, type) => {
    try {
      const res = await api.get(`/rides/driver/${id}/document/${type}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open document"); }
  };
  const review = async (id, approve) => {
    try {
      await api.post(`/rides/admin/drivers/${id}/review`, { approve, inspection_passed: !!insp[id], notes: notes[id] || "" });
      toast.success(approve ? "Driver approved" : "Driver rejected");
      qc.invalidateQueries({ queryKey: ["admin-drivers"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="driver-approvals">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><ShieldCheck size={20} weight="duotone" /></div>
        <div>
          <h2 className="font-display text-lg font-semibold">Driver approvals & inspection</h2>
          <p className="text-sm text-muted-foreground">Verify documents and confirm the vehicle inspection before a driver can go online.</p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {drivers.length === 0 && <p className="text-sm text-muted-foreground">No driver applications yet.</p>}
        {drivers.map((d) => (
          <div key={d.user_id} data-testid={`driver-row-${d.user_id}`} className="rounded-xl border border-white/10 bg-background p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{d.name} <span className="text-xs text-muted-foreground">· {d.email}</span></p>
                <p className="text-xs text-muted-foreground">
                  {CATS.find((c) => c.key === d.vehicle_type)?.emoji} {d.vehicle_color} {d.vehicle_make} {d.vehicle_model} · {d.vehicle_plate || "no plate"} · License {d.license_number || "—"}
                </p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${d.approval_status === "approved" ? "bg-emerald-500/15 text-emerald-600" : d.approval_status === "rejected" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"}`}>{d.approval_status}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {["license", "registration", "insurance", "photo"].map((t) => (
                <button key={t} data-testid={`doc-view-${d.user_id}-${t}`} disabled={!d.documents?.includes(t)}
                  onClick={() => viewDoc(d.user_id, t)}
                  className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold disabled:opacity-30">
                  <Eye size={13} /> {t}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" data-testid={`insp-${d.user_id}`} checked={!!insp[d.user_id]}
                  onChange={(e) => setInsp((x) => ({ ...x, [d.user_id]: e.target.checked }))} className="h-4 w-4" />
                Vehicle inspection passed
              </label>
              <input data-testid={`notes-${d.user_id}`} value={notes[d.user_id] || ""} onChange={(e) => setNotes((x) => ({ ...x, [d.user_id]: e.target.value }))}
                placeholder="Review notes (optional)"
                className="min-w-[180px] flex-1 rounded-xl border border-white/10 bg-card px-3 py-2 text-xs outline-none focus:border-primary" />
              <button data-testid={`approve-${d.user_id}`} onClick={() => review(d.user_id, true)}
                className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Approve</button>
              <button data-testid={`reject-${d.user_id}`} onClick={() => review(d.user_id, false)}
                className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold hover:text-destructive">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Promos() {
  const qc = useQueryClient();
  const [f, setF] = useState({ code: "", kind: "percent", value: "", scope: "all" });
  const { data } = useQuery({ queryKey: ["promos"], queryFn: async () => (await api.get("/promos")).data.promos });
  const create = async () => {
    if (!f.code || !f.value) { toast.error("Enter a code and value"); return; }
    try {
      await api.post("/promos", { code: f.code, kind: f.kind, value: Number(f.value), scope: f.scope, active: true });
      toast.success("Promo created"); setF({ code: "", kind: "percent", value: "", scope: "all" });
      qc.invalidateQueries({ queryKey: ["promos"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/promos/${id}`); qc.invalidateQueries({ queryKey: ["promos"] }); } catch (e) { toast.error(apiError(e)); } };
  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="admin-promos">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Percent size={20} weight="duotone" /></div>
        <div><h2 className="font-display text-lg font-semibold">Promo codes</h2><p className="text-sm text-muted-foreground">Discounts applied to Ride fares and Eat orders (wallet payments). Platform-funded — drivers & restaurants still earn full share.</p></div>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-4">
        <input data-testid="promo-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="CODE" className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm uppercase outline-none focus:border-primary" />
        <select data-testid="promo-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary"><option value="percent">% off</option><option value="fixed">$ off (cents)</option></select>
        <input data-testid="promo-value" type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder={f.kind === "percent" ? "e.g. 20" : "cents e.g. 200"} className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <select data-testid="promo-scope" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary"><option value="all">Ride & Eat</option><option value="ride">Ride only</option><option value="eat">Eat only</option></select>
      </div>
      <button data-testid="promo-create" onClick={create} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Create promo</button>
      <div className="mt-4 space-y-2">
        {(data || []).map((p) => (
          <div key={p.id} data-testid={`promo-row-${p.code}`} className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
            <span><span className="font-semibold">{p.code}</span> · {p.kind === "percent" ? `${p.value}%` : `$${(p.value / 100).toFixed(2)}`} · {p.scope} · used {p.uses}{p.max_uses ? `/${p.max_uses}` : ""}</span>
            <button data-testid={`promo-del-${p.code}`} onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Payouts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-payouts"], queryFn: async () => (await api.get("/payouts/admin")).data.payouts });
  const rows = (data || []).slice().sort((a, b) => (a.status === "pending" ? -1 : 1));
  const process = async (id, approve) => {
    try { await api.post(`/payouts/admin/${id}/process`, { approve, note: "" }); toast.success(approve ? "Marked paid" : "Rejected & refunded"); qc.invalidateQueries({ queryKey: ["admin-payouts"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="admin-payouts">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Coins size={20} weight="duotone" /></div>
        <div><h2 className="font-display text-lg font-semibold">Driver payouts</h2><p className="text-sm text-muted-foreground">Approve Mobile Money cash-outs. Approving marks it paid; rejecting refunds the driver's wallet. <span className="font-semibold text-amber-600">Transfer is manual (no live MoMo API yet).</span></p></div>
      </div>
      <div className="mt-4 space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No payout requests.</p>}
        {rows.map((p) => (
          <div key={p.id} data-testid={`payout-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-background px-3 py-2 text-sm">
            <span>{p.user_email} · <span className="font-semibold">{money(p.amount_cents)}</span> → {p.momo_provider.toUpperCase()} {p.momo_number}</span>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "paid" ? "bg-emerald-500/15 text-emerald-600" : p.status === "rejected" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"}`}>{p.status}</span>
              {p.status === "pending" && <>
                <button data-testid={`payout-approve-${p.id}`} onClick={() => process(p.id, true)} className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Mark paid</button>
                <button data-testid={`payout-reject-${p.id}`} onClick={() => process(p.id, false)} className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold hover:text-destructive">Reject</button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminRides() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await api.get("/settings/platform")).data });
  const s = data?.settings;

  const save = async (patch) => {
    setSaving(true);
    try { await api.put("/settings/platform", patch); toast.success("Saved"); qc.invalidateQueries({ queryKey: ["platform-settings"] }); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  const pct = s.ride_commission_pct ?? 0;
  const xafRate = s.xaf_per_usd ?? 600;
  const surgeMult = s.surge_multiplier ?? 1.5;
  const surgeHours = (s.surge_hours || []).join(",");

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-rides">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">iYeneh Ride</h1>
        <p className="mt-1 text-sm text-muted-foreground">Set fares per vehicle type, your commission, and approve drivers. Changes apply to new trips immediately.</p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Car size={20} weight="duotone" /></div>
          <div><h2 className="font-display text-lg font-semibold">Availability</h2><p className="text-sm text-muted-foreground">Turn the iYeneh Ride module on or off.</p></div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="rides-enabled" checked={!!s.rides_enabled} onChange={(e) => save({ rides_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Rides enabled</span>
        </label>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Fares by vehicle type</h2>
        <p className="mt-1 text-sm text-muted-foreground">Fare = base + (per-km × distance), never below the minimum.</p>
        <div className="mt-4 space-y-4">
          {CATS.map((c) => (
            <div key={c.key} className="rounded-xl border border-white/10 bg-background p-4" data-testid={`cat-${c.key}`}>
              <p className="mb-3 text-sm font-semibold">{c.emoji} {c.label}</p>
              <div className="grid grid-cols-3 gap-3">
                <Dollar label="Base fare" testid={`${c.key}-base`} value={s[`ride_${c.key}_base_cents`]} onSave={(v) => save({ [`ride_${c.key}_base_cents`]: v })} />
                <Dollar label="Per km" testid={`${c.key}-perkm`} value={s[`ride_${c.key}_per_km_cents`]} onSave={(v) => save({ [`ride_${c.key}_per_km_cents`]: v })} />
                <Dollar label="Minimum" testid={`${c.key}-min`} value={s[`ride_${c.key}_min_cents`]} onSave={(v) => save({ [`ride_${c.key}_min_cents`]: v })} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Your commission</h2>
        <p className="mt-1 text-sm text-muted-foreground">Percentage of every fare the platform keeps; the driver gets the rest.</p>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="ride-commission" type="number" min="0" max="100" step="1" defaultValue={pct.toString()}
            onBlur={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); if (v !== pct) save({ ride_commission_pct: v }); }}
            className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <span className="text-sm text-muted-foreground">% of each fare</span>
          <CheckCircle size={14} weight="fill" className="ml-2 text-primary" />
        </div>
      </section>

      <DriverApprovals />

      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="admin-currency-surge">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Lightning size={20} weight="duotone" /></div>
          <div><h2 className="font-display text-lg font-semibold">Currency & surge</h2><p className="text-sm text-muted-foreground">Set the XAF display rate and busy-hour surge pricing for rides.</p></div>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <div>
            <p className="text-sm font-medium">XAF per $1 (display)</p>
            <input data-testid="xaf-rate" type="number" min="1" step="1" defaultValue={xafRate.toString()}
              onBlur={(e) => { const v = Math.max(1, Number(e.target.value) || 600); if (v !== xafRate) save({ xaf_per_usd: v }); }}
              className="mt-2 w-32 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-white/10 p-3">
            <input type="checkbox" data-testid="surge-enabled" checked={!!s.surge_enabled} onChange={(e) => save({ surge_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span className="text-sm">Surge pricing enabled<span className="mt-0.5 block text-xs text-muted-foreground">Multiplies fares during busy hours.</span></span>
          </label>
          <div>
            <p className="text-sm font-medium">Surge multiplier</p>
            <input data-testid="surge-multiplier" type="number" min="1" max="5" step="0.1" defaultValue={surgeMult.toString()}
              onBlur={(e) => { const v = Math.max(1, Math.min(5, Number(e.target.value) || 1.5)); if (v !== surgeMult) save({ surge_multiplier: v }); }}
              className="mt-2 w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Busy hours (UTC, comma-sep)</p>
            <input data-testid="surge-hours" defaultValue={surgeHours} placeholder="e.g. 7,8,17,18 — blank = all hours"
              onBlur={(e) => { const arr = e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => n >= 0 && n <= 23); if (arr.join(",") !== surgeHours) save({ surge_hours: arr }); }}
              className="mt-2 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
      </section>

      <Promos />
      <Payouts />

      {saving && <p className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin" /> Saving…</p>}
    </div>
  );
}
